import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageCard } from '../../components/layout/page/PageCard';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { InlineAlert } from '../../components/ui/InlineAlert';
import { RequiredMark } from '../../components/ui/RequiredMark';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { listCampaignTags, ProspectCampaignTag } from '../../services/campaignTagsApi';
import {
  createProspect,
  deleteProspect,
  enrichProspect,
  getProspect,
  ProspectUpsertPayload,
  updateProspect,
} from '../../services/prospectsApi';
import { listSalesProducts, SalesProductSummary } from '../../services/salesProductsApi';
import { BuyerPersona, listBuyerPersonas } from '../../services/buyerPersonasApi';
import { ProspectPersonaLinkItemPayload, ProspectPersonaSource } from '../../services/prospectsApi';
import { convertProspectToOpportunity } from '../../services/salesOpportunitiesApi';
import { Company, listCompanies } from '../../services/coreCompaniesApi';
import { CityAutocomplete } from '../../components/ui/CityAutocomplete';
import { CompanyAutocomplete } from '../../components/ui/CompanyAutocomplete';
import { formatPhoneMask } from '../../utils/formatPhoneMask';
import { CityOption, CountryOption, listCities, listCountries, listStates, StateOption } from '../../services/locationsApi';
import { CompanyQuickCreateModal } from '../../components/opportunities/CompanyQuickCreateModal';

const emptyForm: ProspectUpsertPayload = {
  fullName: '',
  title: '',
  companyId: undefined,
  companyName: '',
  companyDomain: '',
  location: '',
  linkedinUrl: '',
  companyLinkedinUrl: '',
  email: '',
  phone: '',
  mobilePhone: '',
  captureSource: 'manual',
  campaignTag: '',
  productIds: [],
  personaLinks: [],
};

type DraftPersonaLink = ProspectPersonaLinkItemPayload & {
  name: string;
};

const PERSONA_SOURCE_OPTIONS: { value: ProspectPersonaSource; label: string }[] = [
  { value: 'Manual', label: 'Manual' },
  { value: 'Inferred', label: 'Inferido' },
];

export function ProspectEditPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const location = useLocation();
  const isLeadRoute = location.pathname.startsWith('/sales/leads');
  const { canWriteModule, canReadModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('sales.prospects');
  const canConvertOpportunity = canWriteModule('sales.opportunities');
  const canManageProducts = canReadModule('sales.products');
  const canManagePersonas = canReadModule('sales.buyer-personas');
  const canSelectCompany = canReadModule('core.companies');
  const canCreateCompany = canWriteModule('core.companies');

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [form, setForm] = useState<ProspectUpsertPayload>(emptyForm);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [campaignTags, setCampaignTags] = useState<ProspectCampaignTag[]>([]);
  const [products, setProducts] = useState<SalesProductSummary[]>([]);
  const [personas, setPersonas] = useState<BuyerPersona[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [personaLinks, setPersonaLinks] = useState<DraftPersonaLink[]>([]);
  const [enrichmentStatus, setEnrichmentStatus] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(true);
  const [funnelStage, setFunnelStage] = useState('Captured');
  const [converting, setConverting] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [states, setStates] = useState<StateOption[]>([]);
  const [cities, setCities] = useState<CityOption[]>([]);
  const [countryId, setCountryId] = useState('');
  const [stateId, setStateId] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const tagResult = await listCampaignTags({ page: 1, pageSize: 200 });
        setCampaignTags(tagResult.items);

        if (canManageProducts) {
          const productResult = await listSalesProducts({ activeOnly: true, page: 1, pageSize: 200 });
          setProducts(productResult.items);
        } else {
          setProducts([]);
        }

        if (canManagePersonas) {
          const personaResult = await listBuyerPersonas({ activeOnly: true, page: 1, pageSize: 200 });
          setPersonas(personaResult.items);
        } else {
          setPersonas([]);
        }

        if (canSelectCompany) {
          const companyResult = await listCompanies({ activeOnly: true, page: 1, pageSize: 500 });
          setCompanies(companyResult.items);
        } else {
          setCompanies([]);
        }
      } catch {
        toast.error('Falha ao carregar dados auxiliares.');
      }
    })();
  }, [canManageProducts, canManagePersonas, canSelectCompany]);

  useEffect(() => {
    void listCountries()
      .then(setCountries)
      .catch(() => toast.error('Falha ao carregar países.'));
  }, []);

  useEffect(() => {
    if (loading || countryId || countries.length === 0) return;
    const brazil = countries.find((country) => country.name === 'Brasil');
    if (brazil) setCountryId(brazil.id);
  }, [loading, countryId, countries]);

  useEffect(() => {
    if (!countryId) {
      setStates([]);
      return;
    }

    void listStates(countryId)
      .then(setStates)
      .catch(() => toast.error('Falha ao carregar estados.'));
  }, [countryId]);

  useEffect(() => {
    if (!stateId) {
      setCities([]);
      return;
    }

    void listCities(stateId)
      .then(setCities)
      .catch(() => toast.error('Falha ao carregar cidades.'));
  }, [stateId]);

  useEffect(() => {
    if (isNew) return;

    void (async () => {
      setLoading(true);
      try {
        const prospect = await getProspect(id!);
        setForm({
          fullName: prospect.fullName,
          title: prospect.title ?? '',
          companyId: prospect.companyId,
          companyName: prospect.companyName ?? '',
          companyDomain: prospect.companyDomain ?? '',
          location: prospect.cityId ? '' : (prospect.location ?? ''),
          cityId: prospect.cityId,
          linkedinUrl: prospect.linkedinUrl ?? '',
          companyLinkedinUrl: prospect.companyLinkedinUrl ?? '',
          email: prospect.email ?? '',
          phone: formatPhoneMask(prospect.phone, 'landline'),
          mobilePhone: formatPhoneMask(prospect.mobilePhone, 'mobile'),
          captureSource: prospect.captureSource ?? '',
          campaignTag: prospect.campaignTag ?? '',
          productIds: prospect.products.map((product) => product.id),
        });
        setSelectedProductIds(new Set(prospect.products.map((product) => product.id)));
        setPersonaLinks(
          prospect.buyerPersonas.map((link) => ({
            buyerPersonaId: link.buyerPersonaId,
            name: link.name,
            confidenceScore: link.confidenceScore,
            source: link.source,
          })),
        );
        setEnrichmentStatus(prospect.enrichmentStatus);
        setCanEdit(prospect.actions?.canEdit ?? true);
        setFunnelStage(prospect.funnelStage);
        setCountryId(prospect.countryId ?? '');
        setStateId(prospect.stateId ?? '');
        const leadRecord = prospect.funnelStage === 'Lead' || prospect.funnelStage === 'Lost';
        const onLeadRoute = location.pathname.startsWith('/sales/leads');
        if (leadRecord && !onLeadRoute) {
          navigate(`/sales/leads/${prospect.id}/edit`, { replace: true });
        } else if (!leadRecord && onLeadRoute) {
          navigate(`/sales/prospects/${prospect.id}/edit`, { replace: true });
        }
      } catch {
        toast.error('Falha ao carregar prospect.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  function toggleProduct(productId: string): void {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }

  const selectedPersonaIds = useMemo(
    () => new Set(personaLinks.map((link) => link.buyerPersonaId)),
    [personaLinks],
  );

  function togglePersona(persona: BuyerPersona): void {
    setPersonaLinks((current) => {
      if (current.some((link) => link.buyerPersonaId === persona.id)) {
        return current.filter((link) => link.buyerPersonaId !== persona.id);
      }

      return [
        ...current,
        {
          buyerPersonaId: persona.id,
          name: persona.name,
          confidenceScore: 80,
          source: 'Manual' as ProspectPersonaSource,
        },
      ];
    });
  }

  function updatePersonaLink(
    buyerPersonaId: string,
    patch: Partial<Pick<DraftPersonaLink, 'confidenceScore' | 'source'>>,
  ): void {
    setPersonaLinks((current) =>
      current.map((link) => (link.buyerPersonaId === buyerPersonaId ? { ...link, ...patch } : link)),
    );
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || (!isNew && !canEdit)) return;

    const creatingLead = isNew && isLeadRoute;
    const trimmedName = form.fullName.trim();
    if (!trimmedName) {
      toast.error(creatingLead ? 'Informe o nome do lead.' : 'Informe o nome do prospect.');
      return;
    }

    const trimmedEmail = form.email?.trim() ?? '';
    if (creatingLead && !trimmedEmail) {
      toast.error('Informe o e-mail do lead.');
      return;
    }

    const payload: ProspectUpsertPayload = {
      ...form,
      fullName: trimmedName,
      email: trimmedEmail || undefined,
      productIds: Array.from(selectedProductIds),
      personaLinks: personaLinks.map((link) => ({
        buyerPersonaId: link.buyerPersonaId,
        confidenceScore: link.confidenceScore ?? 0,
        source: link.source ?? 'Manual',
      })),
    };

    setSaving(true);
    try {
      const saved = isNew
        ? await createProspect(payload)
        : await updateProspect(id!, payload);
      const savedIsLead = saved.funnelStage === 'Lead' || saved.funnelStage === 'Lost';
      toast.success(
        savedIsLead
          ? 'Lead salvo.'
          : isNew
            ? 'Prospect criado.'
            : 'Prospect atualizado.',
      );
      navigate(
        savedIsLead ? `/sales/leads/${saved.id}/edit` : `/sales/prospects/${saved.id}/edit`,
        { replace: true },
      );
    } catch {
      toast.error(isNew ? 'Falha ao criar prospect.' : 'Falha ao atualizar prospect.');
    } finally {
      setSaving(false);
    }
  }

  async function handleEnrich(): Promise<void> {
    if (!canWrite || isNew) return;
    setEnriching(true);
    try {
      await enrichProspect(id!);
      toast.success('Enriquecimento enfileirado (Apollo, Hunter, Dropcontact).');
      setEnrichmentStatus('InProgress');
    } catch {
      toast.error('Falha ao enfileirar enriquecimento.');
    } finally {
      setEnriching(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!canWrite || isNew) return;
    if (
      !(await confirm({
        title: 'Excluir prospect',
        message: 'Excluir este prospect?',
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }
    try {
      await deleteProspect(id!);
      const returnToLeads = isLeadRoute || funnelStage === 'Lead' || funnelStage === 'Lost';
      toast.success(returnToLeads ? 'Lead excluído.' : 'Prospect excluído.');
      navigate(returnToLeads ? '/sales/leads' : '/sales/prospects');
    } catch {
      toast.error('Falha ao excluir prospect.');
    }
  }

  async function handleConvertToOpportunity(): Promise<void> {
    if (!canConvertOpportunity || isNew || !id) return;
    setConverting(true);
    try {
      const opportunity = await convertProspectToOpportunity({
        prospectId: id,
        amount: 0,
        monthlyRecurringAmount: 0,
        revenueType: 'Recurring',
        stage: 'Qualified',
        probabilityPercent: 30,
        salesProductId: Array.from(selectedProductIds)[0],
      });
      toast.success('Oportunidade criada a partir do lead.');
      navigate(`/sales/opportunities?opportunityId=${opportunity.id}`);
    } catch {
      toast.error('Falha ao converter lead em oportunidade.');
    } finally {
      setConverting(false);
    }
  }

  const creatingLead = isNew && isLeadRoute;
  const isLeadView = creatingLead || (!isNew && (isLeadRoute || funnelStage === 'Lead' || funnelStage === 'Lost'));
  const canCreateOpportunity = canConvertOpportunity && funnelStage === 'Lead';
  const formDisabled = !canWrite || (!isNew && !canEdit);

  if (loading) {
    return <p className="text-secondary-500">Carregando...</p>;
  }

  return (
    <PageShell variant="form">
      <PageHeader
        title={creatingLead ? 'Novo lead' : isNew ? 'Novo prospect' : isLeadView ? 'Editar lead' : 'Editar prospect'}
        description={
          creatingLead
            ? 'Cadastre um contato já identificado para a abordagem comercial.'
            : isLeadView
              ? 'Contato pronto para a abordagem comercial. Crie a oportunidade quando a venda começar.'
              : 'Pessoa em prospecção. Com o contato identificado, ela passa a ser um lead.'
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={isLeadView ? '/sales/leads' : '/sales/prospects'}
              className="erp-btn-secondary"
            >
              Voltar
            </Link>
            {canCreateOpportunity ? (
              <button
                type="button"
                className="erp-btn-primary"
                disabled={converting}
                onClick={() => void handleConvertToOpportunity()}
              >
                {converting ? 'Criando...' : 'Criar oportunidade'}
              </button>
            ) : null}
          </div>
        }
      />

      {!isNew && enrichmentStatus ? (
        <p className="erp-hint">
          Status de enriquecimento: <strong>{enrichmentStatus}</strong>
        </p>
      ) : null}

      {!isNew && !canEdit ? (
        <InlineAlert tone="warning">
          Este lead não pode mais ser editado. Você pode marcá-lo como perdido ou removê-lo da listagem.
        </InlineAlert>
      ) : null}

      <PageCard>
        <form onSubmit={(event) => void handleSubmit(event)} className="erp-form">
        <div className="erp-form-grid">
          <label className="erp-label sm:col-span-2">
            Nome completo <RequiredMark />
            <input
              className="erp-input"
              placeholder="Nome e sobrenome"
              autoComplete="name"
              value={form.fullName}
              onChange={(event) => setForm((current) => ({ ...current, fullName: event.target.value }))}
              required
              disabled={formDisabled}
            />
          </label>
          <label className="erp-label sm:col-span-2">
            E-mail {creatingLead ? <RequiredMark /> : null}
            <input
              type="email"
              className="erp-input"
              placeholder="nome@empresa.com"
              autoComplete="email"
              value={form.email ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              required={creatingLead}
              disabled={formDisabled}
            />
          </label>
          <label className="erp-label">
            Telefone
            <input
              type="tel"
              className="erp-input"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="(11) 3333-4444"
              maxLength={14}
              value={form.phone ?? ''}
              onChange={(event) =>
                setForm((current) => ({ ...current, phone: formatPhoneMask(event.target.value, 'landline') }))
              }
              disabled={formDisabled}
            />
          </label>
          <label className="erp-label">
            Celular
            <input
              type="tel"
              className="erp-input"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="(11) 98888-7777"
              maxLength={15}
              value={form.mobilePhone ?? ''}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  mobilePhone: formatPhoneMask(event.target.value, 'mobile'),
                }))
              }
              disabled={formDisabled}
            />
          </label>
          <label className="erp-label">
            Cargo
            <input
              className="erp-input"
              placeholder="Ex.: Diretor comercial"
              autoComplete="organization-title"
              value={form.title ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
              disabled={formDisabled}
            />
          </label>
          <label className="erp-label">
            Empresa
            <CompanyAutocomplete
              companies={companies}
              companyId={form.companyId}
              disabled={formDisabled || !canSelectCompany}
              canCreate={canCreateCompany}
              onSelect={(company) =>
                setForm((current) => ({
                  ...current,
                  companyId: company?.id,
                  companyName: company?.name ?? '',
                }))
              }
              onCreate={() => setCompanyModalOpen(true)}
            />
          </label>
          <label className="erp-label">
            Domínio da empresa
            <input
              className="erp-input"
              placeholder="exemplo.com.br"
              value={form.companyDomain ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, companyDomain: event.target.value }))}
              disabled={formDisabled}
            />
          </label>
          <label className="erp-label">
            País
            <select
              className="erp-input"
              value={countryId}
              onChange={(event) => {
                setCountryId(event.target.value);
                setStateId('');
                setForm((current) => ({ ...current, cityId: undefined, location: '' }));
              }}
              disabled={formDisabled}
            >
              <option value="">Selecione o país</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            Estado
            <select
              className="erp-input"
              value={stateId}
              onChange={(event) => {
                setStateId(event.target.value);
                setForm((current) => ({ ...current, cityId: undefined, location: '' }));
              }}
              disabled={formDisabled || !countryId}
            >
              <option value="">Selecione o estado</option>
              {states.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name}
                </option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            Cidade
            <CityAutocomplete
              cities={cities}
              cityId={form.cityId}
              disabled={formDisabled || !stateId}
              onSelect={(city) =>
                setForm((current) => ({
                  ...current,
                  cityId: city?.id,
                  location: '',
                }))
              }
            />
          </label>
          {form.location && !form.cityId ? (
            <p className="erp-hint sm:col-span-2">
              Localização informada antes do catálogo: {form.location}
            </p>
          ) : null}
          <label className="erp-label">
            LinkedIn (pessoa)
            <input
              className="erp-input"
              placeholder="https://www.linkedin.com/in/seu-perfil"
              value={form.linkedinUrl ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, linkedinUrl: event.target.value }))}
              disabled={formDisabled}
            />
          </label>
          <label className="erp-label">
            LinkedIn (empresa)
            <input
              className="erp-input"
              placeholder="https://www.linkedin.com/company/empresa"
              value={form.companyLinkedinUrl ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, companyLinkedinUrl: event.target.value }))}
              disabled={formDisabled}
            />
          </label>
        </div>

        <div className="erp-form-grid">
          <label className="erp-label">
            Origem da captura
            <input
              className="erp-input"
              placeholder="Ex.: LinkedIn, site, indicação"
              value={form.captureSource ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, captureSource: event.target.value }))}
              disabled={formDisabled}
            />
          </label>
          <label className="erp-label">
            Campanha
            <select
              className="erp-input"
              value={form.campaignTag ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, campaignTag: event.target.value }))}
              disabled={formDisabled}
            >
              <option value="">Sem campanha</option>
              {campaignTags.map((tag) => (
                <option key={tag.id} value={tag.name}>
                  {tag.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {canManageProducts ? (
        <div>
          <p className="text-sm font-medium text-secondary-800">Produtos vinculados</p>
          <p className="erp-hint mb-2">Selecione os produtos comerciais associados a este lead.</p>
          {products.length === 0 ? (
            <p className="text-sm text-secondary-500">Nenhum produto ativo cadastrado.</p>
          ) : (
            <div className="erp-form-grid">
              {products.map((product) => (
                <label
                  key={product.id}
                  className="flex items-center gap-2 rounded-lg border border-secondary-200 px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedProductIds.has(product.id)}
                    onChange={() => toggleProduct(product.id)}
                    disabled={formDisabled}
                  />
                  {product.logoBase64 ? (
                    <img src={product.logoBase64} alt="" className="h-6 w-6 object-contain" />
                  ) : null}
                  <span>{product.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        ) : null}

        {canManagePersonas ? (
        <div>
          <p className="text-sm font-medium text-secondary-800">Personas / ICP</p>
          <p className="erp-hint mb-2">
            Classifique o prospect com personas compradoras para campanhas e relatórios.
          </p>
          {personas.length === 0 ? (
            <p className="text-sm text-secondary-500">Nenhuma persona ativa cadastrada.</p>
          ) : (
            <ul className="space-y-2">
              {personas.map((persona) => {
                const selected = selectedPersonaIds.has(persona.id);
                const link = personaLinks.find((item) => item.buyerPersonaId === persona.id);

                return (
                  <li key={persona.id} className="rounded-lg border border-secondary-200 px-3 py-2">
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={selected}
                        disabled={formDisabled}
                        onChange={() => togglePersona(persona)}
                      />
                      <span className="flex-1 text-sm">
                        <span className="font-medium text-secondary-900">{persona.name}</span>
                        {persona.jobTitle ? (
                          <span className="ml-2 text-secondary-600">— {persona.jobTitle}</span>
                        ) : null}
                      </span>
                    </label>

                    {selected && link ? (
                      <div className="mt-2 ml-7 flex flex-wrap items-center gap-4">
                        <label className="erp-label-inline text-sm">
                          Confiança
                          <input
                            type="number"
                            min={0}
                            max={100}
                            className="erp-input ml-2 w-20"
                            disabled={formDisabled}
                            value={link.confidenceScore ?? 0}
                            onChange={(event) =>
                              updatePersonaLink(persona.id, {
                                confidenceScore: Number(event.target.value),
                              })
                            }
                          />
                        </label>
                        <label className="erp-label-inline text-sm">
                          Origem
                          <select
                            className="erp-input ml-2"
                            disabled={formDisabled}
                            value={link.source ?? 'Manual'}
                            onChange={(event) =>
                              updatePersonaLink(persona.id, {
                                source: event.target.value as ProspectPersonaSource,
                              })
                            }
                          >
                            {PERSONA_SOURCE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        ) : null}

        {canWrite ? (
          <div className="erp-btn-actions">
            <button type="submit" disabled={saving} className="erp-btn-primary">
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            {!isNew ? (
              <>
                <button
                  type="button"
                  disabled={enriching}
                  className="erp-btn-integration"
                  onClick={() => void handleEnrich()}
                >
                  {enriching ? 'Enfileirando...' : 'Iniciar enriquecimento'}
                </button>
                <button
                  type="button"
                  className="erp-btn-danger"
                  onClick={() => void handleDelete()}
                >
                  Excluir
                </button>
              </>
            ) : null}
          </div>
        ) : null}
        </form>
      </PageCard>
      <CompanyQuickCreateModal
          open={companyModalOpen}
          description={
            isLeadView
              ? 'Cadastro rápido para vincular ao lead. Você pode completar os dados depois.'
              : 'Cadastro rápido para vincular ao prospect. Você pode completar os dados depois.'
          }
          onClose={() => setCompanyModalOpen(false)}
          onCreated={(company) => {
            setCompanies((current) => [company, ...current.filter((item) => item.id !== company.id)]);
            setForm((current) => ({
              ...current,
              companyId: company.id,
              companyName: company.name,
            }));
            setCompanyModalOpen(false);
          }}
        />
    </PageShell>
  );
}
